// AdminRetailDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../firebase/config';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  Timestamp,
  query,
  orderBy,
  getDoc
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import './Dashboard.css';
import toast, { Toaster } from 'react-hot-toast';

const AdminRetailDashboard = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedShop, setSelectedShop] = useState(null);
  const [showShopModal, setShowShopModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedShopId, setSelectedShopId] = useState(null);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('23:59');
   const [copiedToken, setCopiedToken] = useState(null);

  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      setLoading(true);
      toast.loading('Loading retail shops...', { id: 'loading' });
      
      const shopsRef = collection(db, 'retail');
      const q = query(shopsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const shopsList = [];
      
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const shopId = docSnap.id;
        
        // Calculate premium status
        let premiumStatus = {};
        if (data.premiumUntil) {
          premiumStatus = {
            isPremiumActive: checkPremiumActive(data.premiumUntil),
            daysLeftInPremium: calculateDaysLeftInPremium(data.premiumUntil),
            premiumEndDate: formatFirestoreDate(data.premiumUntil)
          };
        }
        
        shopsList.push({
          id: shopId,
          ...data,
          ...premiumStatus,
          // Format dates for display
          formattedCreatedAt: formatFirestoreDate(data.createdAt),
          formattedUpdatedAt: formatFirestoreDate(data.updatedAt),
          formattedLastLogin: formatFirestoreDate(data.lastLogin),
          formattedLastPlanActivation: formatFirestoreDate(data.lastPlanActivation),
          formattedPremiumUntil: formatFirestoreDate(data.premiumUntil)
        });
      });
      
      setShops(shopsList);
      toast.success(`Loaded ${shopsList.length} retail shops`, { id: 'loading' });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching shops:', error);
      toast.error(`Failed to load shops: ${error.message}`);
      setLoading(false);
    }
  };

  const checkPremiumActive = (premiumUntil) => {
    if (!premiumUntil) return false;
    try {
      const premiumDate = premiumUntil.toDate ? premiumUntil.toDate() : new Date(premiumUntil);
      return premiumDate > new Date();
    } catch (e) {
      return false;
    }
  };

  const calculateDaysLeftInPremium = (premiumUntil) => {
    if (!premiumUntil) return 0;
    try {
      const endDate = premiumUntil.toDate ? premiumUntil.toDate() : new Date(premiumUntil);
      const today = new Date();
      const diffTime = endDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    } catch (e) {
      return 0;
    }
  };

  const formatFirestoreDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Other functions के साथ add करो:
const copyFCMToken = (token) => {
  navigator.clipboard.writeText(token).then(() => {
    setCopiedToken(token);
    toast.success('FCM Token copied to clipboard!');
    setTimeout(() => setCopiedToken(null), 2000);
  }).catch(err => {
    console.error('Failed to copy token: ', err);
    toast.error('Failed to copy token');
  });
};

  const openPlanModal = (shopId) => {
    setSelectedShopId(shopId);
    const shop = shops.find(s => s.id === shopId);
    if (shop && shop.premiumUntil) {
      const premiumDate = shop.premiumUntil.toDate ? shop.premiumUntil.toDate() : new Date(shop.premiumUntil);
      setCustomDate(premiumDate.toISOString().split('T')[0]);
      setCustomTime(premiumDate.toTimeString().split(' ')[0].substring(0, 5));
    } else {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setCustomDate(tomorrow.toISOString().split('T')[0]);
      setCustomTime('23:59');
    }
    setShowPlanModal(true);
  };

  const activateCustomPlan = async () => {
    try {
      if (!selectedShopId || !customDate) {
        toast.error('Please select a date');
        return;
      }

      toast.loading('Updating premium plan...', { id: 'activate' });

      const shopRef = doc(db, 'retail', selectedShopId);
      const shopSnap = await getDoc(shopRef);
      const shopData = shopSnap.data();

      const expiryDate = new Date(`${customDate}T${customTime}`);
      const today = new Date();
      const diffTime = expiryDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const diffMonths = Math.floor(diffDays / 30);
      const remainingDays = diffDays % 30;

      let durationText = '';
      if (diffMonths > 0) {
        durationText = `${diffMonths} month${diffMonths > 1 ? 's' : ''}`;
        if (remainingDays > 0) {
          durationText += ` ${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
        }
      } else {
        durationText = `${diffDays} day${diffDays > 1 ? 's' : ''}`;
      }

      const isUpdating = shopData.planType === 'premium';
      const action = isUpdating ? 'updated' : 'activated';
      const planName = diffDays <= 0 ? 'Free' : `Retail Premium (${durationText})`;

      await updateDoc(shopRef, {
        planType: diffDays <= 0 ? 'free' : 'premium',
        activePlan: diffDays <= 0 ? 'free' : 'retail_premium',
        planName: planName,
        premiumUntil: diffDays <= 0 ? null : Timestamp.fromDate(expiryDate),
        updatedAt: Timestamp.now(),
        lastPlanActivation: Timestamp.now(),
        customPlanActivated: diffDays > 0,
        customPlanDuration: diffDays > 0 ? durationText : null,
        customPlanExpiry: diffDays > 0 ? expiryDate.toISOString() : null
      });

      // Add to activation history
      const historyEntry = {
        planId: diffDays <= 0 ? 'free' : 'retail_premium',
        planName: diffDays <= 0 ? 'Free Plan' : 'Retail Premium Plan',
        planType: diffDays <= 0 ? 'free' : 'premium',
        activatedAt: Timestamp.now(),
        expiresAt: diffDays <= 0 ? null : Timestamp.fromDate(expiryDate),
        duration: durationText,
        activatedBy: 'admin',
        shopEmail: shopData.email,
        customActivation: true,
        expiryDate: diffDays <= 0 ? null : expiryDate.toISOString(),
        totalDays: diffDays,
        action: isUpdating ? 'updated' : 'activated'
      };

      const currentHistory = shopData.activationHistory || [];
      await updateDoc(shopRef, {
        activationHistory: [...currentHistory, historyEntry]
      });

      toast.success(`Premium plan ${action} until ${formatFirestoreDate(Timestamp.fromDate(expiryDate))}`, {
        id: 'activate',
        duration: 5000
      });

      setShowPlanModal(false);
      setSelectedShopId(null);
      fetchShops();

    } catch (error) {
      console.error('Error updating premium plan:', error);
      toast.error(`Failed to update plan: ${error.message}`, {
        id: 'activate'
      });
    }
  };

  const deactivatePremium = async (shopId) => {
    try {
      toast.loading('Deactivating premium...', { id: 'deactivate' });
      
      const shopRef = doc(db, 'retail', shopId);
      const shopSnap = await getDoc(shopRef);
      const shopData = shopSnap.data();
      
      await updateDoc(shopRef, {
        planType: 'free',
        activePlan: 'free',
        planName: 'Free',
        premiumUntil: null,
        updatedAt: Timestamp.now(),
        premiumDeactivatedAt: Timestamp.now(),
        customPlanActivated: false,
        customPlanDuration: null,
        customPlanExpiry: null
      });

      const historyEntry = {
        action: 'deactivate',
        previousPlan: shopData.activePlan || 'premium',
        deactivatedAt: Timestamp.now(),
        deactivatedBy: 'admin',
        shopEmail: shopData.email,
        reason: 'Admin deactivated'
      };

      const currentHistory = shopData.activationHistory || [];
      await updateDoc(shopRef, {
        activationHistory: [...currentHistory, historyEntry]
      });

      toast.success('Premium deactivated successfully!', {
        id: 'deactivate',
        duration: 3000
      });
      
      fetchShops();
    } catch (error) {
      console.error('Error deactivating premium:', error);
      toast.error(`Error deactivating premium: ${error.message}`, {
        id: 'deactivate'
      });
    }
  };

  const viewShopDetails = (shop) => {
    setSelectedShop(shop);
    setShowShopModal(true);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Logged out successfully');
      window.location.href = '/login';
    } catch (error) {
      toast.error('Error logging out');
    }
  };

  const filteredShops = useMemo(() => {
    let filtered = shops;
    
    // Apply filter type
    if (filterType === 'premium') {
      filtered = filtered.filter(s => s.planType === 'premium' && s.isPremiumActive);
    } else if (filterType === 'free') {
      filtered = filtered.filter(s => s.planType === 'free');
    } else if (filterType === 'active') {
      filtered = filtered.filter(s => s.status === 'active');
    } else if (filterType === 'verified') {
      filtered = filtered.filter(s => s.emailVerified);
    }
    
    // Apply search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(shop => 
        (shop.email && shop.email.toLowerCase().includes(searchLower)) ||
        (shop.shopName && shop.shopName.toLowerCase().includes(searchLower)) ||
        (shop.displayName && shop.displayName.toLowerCase().includes(searchLower)) ||
        (shop.phone && shop.phone.includes(searchTerm)) ||
        (shop.city && shop.city.toLowerCase().includes(searchLower)) ||
        (shop.shopType && shop.shopType.toLowerCase().includes(searchLower))
      );
    }
    
    return filtered;
  }, [shops, searchTerm, filterType]);

  const shopStats = useMemo(() => {
    const total = shops.length;
    const premium = shops.filter(s => s.planType === 'premium' && s.isPremiumActive).length;
    const free = shops.filter(s => s.planType === 'free').length;
    const active = shops.filter(s => s.status === 'active').length;
    const verified = shops.filter(s => s.emailVerified).length;
    
    return { total, premium, free, active, verified };
  }, [shops]);

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading retail shops...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
            borderRadius: '10px',
            padding: '16px'
          },
        }}
      />
      
      {/* Header */}
      <header className="admin-header">
        <div className="header-left">
          <h1>🏪 Retail Admin Dashboard</h1>
        </div>
        
        <div className="header-right">
          <div className="admin-info">
            <span className="admin-email">{auth.currentUser?.email}</span>
            <button onClick={handleLogout} className="btn-logout">
              🔓 Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🏪</div>
          <div className="stat-content">
            <h3>{shopStats.total}</h3>
            <p>Total Shops</p>
          </div>
        </div>
        
        <div className="stat-card premium">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <h3>{shopStats.premium}</h3>
            <p>Premium Shops</p>
          </div>
        </div>
        
        <div className="stat-card free">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <h3>{shopStats.free}</h3>
            <p>Free Shops</p>
          </div>
        </div>
        
        <div className="stat-card active">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{shopStats.active}</h3>
            <p>Active Shops</p>
          </div>
        </div>
        
        <div className="stat-card verified">
          <div className="stat-icon">📧</div>
          <div className="stat-content">
            <h3>{shopStats.verified}</h3>
            <p>Verified Shops</p>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="search-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search shops..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button className="btn-search">🔍</button>
        </div>
        
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            All
          </button>
          <button 
            className={`filter-tab ${filterType === 'premium' ? 'active' : ''}`}
            onClick={() => setFilterType('premium')}
          >
            Premium
          </button>
          <button 
            className={`filter-tab ${filterType === 'free' ? 'active' : ''}`}
            onClick={() => setFilterType('free')}
          >
            Free
          </button>
          <button 
            className={`filter-tab ${filterType === 'active' ? 'active' : ''}`}
            onClick={() => setFilterType('active')}
          >
            Active
          </button>
          <button 
            className={`filter-tab ${filterType === 'verified' ? 'active' : ''}`}
            onClick={() => setFilterType('verified')}
          >
            Verified
          </button>
        </div>
      </div>

      {/* Shops Table */}
      <div className="table-container">
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Shop Information</th>
                <th>Plan Status</th>
                <th>Business Info</th>
                <th>Location</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredShops.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    No retail shops found
                  </td>
                </tr>
              ) : (
                filteredShops.map((shop, index) => (
                  <tr key={shop.id}>
                    <td>{index + 1}</td>
                    <td className="user-cell">
                      <div className="user-avatar">
                        {shop.shopName?.charAt(0) || 'S'}
                      </div>
                      <div className="user-details">
                        <div className="user-name">{shop.shopName || 'No Shop Name'}</div>
                        <div className="user-email">{shop.email || 'No Email'}</div>
                        <div className="user-meta">
                          <span className="user-id">ID: {shop.id.substring(0, 8)}...</span>
                          <span className="user-phone">{shop.phone || 'No Phone'}</span>
                        </div>
                        <div className="user-owner">
                          <small>Owner: {shop.displayName || 'Unknown'}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="plan-status">
                        <span className={`plan-badge ${shop.planType === 'premium' ? 'premium' : 'free'}`}>
                          {shop.planType === 'premium' ? '⭐ Premium' : '🎯 Free'}
                        </span>
                        {shop.premiumUntil && (
                          <div className="plan-expiry">
                            <small>Expires: {shop.formattedPremiumUntil}</small>
                            {shop.daysLeftInPremium > 0 && (
                              <small className="days-left">({shop.daysLeftInPremium} days left)</small>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="business-info">
                        <div className="shop-type">{shop.shopType || 'General Store'}</div>
                        {shop.gstNumber && (
                          <div className="gst-info">
                            <small>GST: {shop.gstNumber}</small>
                          </div>
                        )}
                        {shop.openingTime && shop.closingTime && (
                          <div className="business-hours">
                            <small>Hours: {shop.openingTime} - {shop.closingTime}</small>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="location-info">
                        <div className="city">{shop.city || 'N/A'}</div>
                        <div className="country">{shop.country || 'N/A'}</div>
                        {shop.address && (
                          <div className="address">
                            <small>{shop.address.substring(0, 30)}...</small>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="login-info">
                        {shop.formattedLastLogin}
                        {shop.emailVerified && (
                          <span className="verified-badge">✓ Verified</span>
                        )}
                      </div>
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button 
                          className="btn-action view"
                          onClick={() => viewShopDetails(shop)}
                          title="View Details"
                        >
                          👁️
                        </button>
                        
                        <button 
                          className="btn-action edit"
                          onClick={() => openPlanModal(shop.id)}
                          title={shop.planType === 'premium' ? "Edit Plan" : "Activate Premium"}
                        >
                          {shop.planType === 'premium' ? '✏️' : '⭐'}
                        </button>
                        
                        {shop.planType === 'premium' && (
                          <button 
                            className="btn-action deactivate"
                            onClick={() => deactivatePremium(shop.id)}
                            title="Deactivate Premium"
                          >
                            ❌
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Plan Activation Modal */}
      {showPlanModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Retail Shop Plan Management</h3>
              <button 
                className="modal-close"
                onClick={() => setShowPlanModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Select Expiry Date</label>
                <input
                  type="date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  className="form-input"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div className="form-group">
                <label>Select Expiry Time</label>
                <input
                  type="time"
                  value={customTime}
                  onChange={(e) => setCustomTime(e.target.value)}
                  className="form-input"
                />
              </div>
              
              {customDate && (
                <div className="plan-preview">
                  <h4>Plan Details:</h4>
                  <div className="preview-item">
                    <span>Plan Type:</span>
                    <strong>Retail Premium</strong>
                  </div>
                  <div className="preview-item">
                    <span>Expiry:</span>
                    <strong>{new Date(`${customDate}T${customTime}`).toLocaleString()}</strong>
                  </div>
                  <div className="preview-item">
                    <span>Duration:</span>
                    <strong>
                      {(() => {
                        const expiryDate = new Date(`${customDate}T${customTime}`);
                        const today = new Date();
                        const diffDays = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
                        const months = Math.floor(diffDays / 30);
                        const days = diffDays % 30;
                        
                        if (months > 0) {
                          return `${months} month${months > 1 ? 's' : ''} ${days > 0 ? `${days} day${days > 1 ? 's' : ''}` : ''}`;
                        }
                        return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
                      })()}
                    </strong>
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowPlanModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={activateCustomPlan}
                disabled={!customDate}
              >
                {shops.find(s => s.id === selectedShopId)?.planType === 'premium' ? '✅ Update Plan' : '✅ Activate Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shop Details Modal */}

{showShopModal && selectedShop && (
  <div className="modal-overlay">
    <div className="modal user-modal">
      <div className="modal-header">
        <h3>Shop Details</h3>
        <button 
          className="modal-close"
          onClick={() => setShowShopModal(false)}
        >
          ✕
        </button>
      </div>
      
      <div className="modal-body">
        {/* Shop Profile Section */}
        <div className="user-profile-section">
          <div className="profile-avatar-large">
            {selectedShop.shopName?.charAt(0) || 'S'}
          </div>
          <div className="profile-info">
            <h4>{selectedShop.shopName || 'No Shop Name'}</h4>
            <p className="profile-email">{selectedShop.email || 'No Email'}</p>
            <div className="profile-meta">
              <span className="meta-badge uid">ID: {selectedShop.id}</span>
              <span className="meta-badge phone">📱 {selectedShop.phone || 'No Phone'}</span>
              <span className={`meta-badge ${selectedShop.status}`}>
                {selectedShop.status || 'unknown'}
              </span>
            </div>
          </div>
        </div>

        {/* Shop Details Grid */}
        <div className="details-grid">
          {/* Business Details */}
          <div className="detail-section">
            <h5>Business Details</h5>
            <div className="detail-row">
              <span>Shop Name:</span>
              <strong>{selectedShop.shopName || 'Not Provided'}</strong>
            </div>
            <div className="detail-row">
              <span>Owner Name:</span>
              <strong>{selectedShop.displayName || 'Not Provided'}</strong>
            </div>
            <div className="detail-row">
              <span>Shop Type:</span>
              <strong>{selectedShop.shopType || 'General Store'}</strong>
            </div>
            <div className="detail-row">
              <span>GST Number:</span>
              <strong>{selectedShop.gstNumber || 'Not Provided'}</strong>
            </div>
            <div className="detail-row">
              <span>Business Hours:</span>
              <strong>{selectedShop.openingTime || '9:00 AM'} - {selectedShop.closingTime || '9:00 PM'}</strong>
            </div>
            <div className="detail-row">
              <span>Email Verified:</span>
              <strong className={selectedShop.emailVerified ? 'verified' : 'not-verified'}>
                {selectedShop.emailVerified ? '✅ Yes' : '❌ No'}
              </strong>
            </div>
          </div>

          {/* Plan Details */}
          <div className="detail-section">
            <h5>Plan Details</h5>
            <div className="detail-row">
              <span>Current Plan:</span>
              <strong className={`plan-tag ${selectedShop.planType}`}>
                {selectedShop.planName || selectedShop.planType}
              </strong>
            </div>
            <div className="detail-row">
              <span>Plan Type:</span>
              <strong>{selectedShop.planType || 'free'}</strong>
            </div>
            <div className="detail-row">
              <span>Active Plan:</span>
              <strong>{selectedShop.activePlan || 'free'}</strong>
            </div>
            {selectedShop.premiumUntil && (
              <>
                <div className="detail-row">
                  <span>Premium Until:</span>
                  <strong>{selectedShop.formattedPremiumUntil}</strong>
                </div>
                {selectedShop.daysLeftInPremium > 0 && (
                  <div className="detail-row">
                    <span>Days Left:</span>
                    <strong className="days-left">{selectedShop.daysLeftInPremium} days</strong>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Location & Contact */}
          <div className="detail-section">
            <h5>Location & Contact</h5>
            <div className="detail-row">
              <span>Address:</span>
              <strong>{selectedShop.address || 'N/A'}</strong>
            </div>
            <div className="detail-row">
              <span>City:</span>
              <strong>{selectedShop.city || 'N/A'}</strong>
            </div>
            <div className="detail-row">
              <span>Country:</span>
              <strong>{selectedShop.country || 'N/A'}</strong>
            </div>
            <div className="detail-row">
              <span>Pincode:</span>
              <strong>{selectedShop.pincode || 'N/A'}</strong>
            </div>
            <div className="detail-row">
              <span>Phone:</span>
              <strong>{selectedShop.phone || 'N/A'}</strong>
            </div>
          </div>

          {/* Timestamps */}
          <div className="detail-section">
            <h5>Timestamps</h5>
            <div className="detail-row">
              <span>Created At:</span>
              <strong>{selectedShop.formattedCreatedAt}</strong>
            </div>
            <div className="detail-row">
              <span>Last Updated:</span>
              <strong>{selectedShop.formattedUpdatedAt}</strong>
            </div>
            <div className="detail-row">
              <span>Last Login:</span>
              <strong>{selectedShop.formattedLastLogin}</strong>
            </div>
            {selectedShop.lastPlanActivation && (
              <div className="detail-row">
                <span>Last Plan Activation:</span>
                <strong>{selectedShop.formattedLastPlanActivation}</strong>
              </div>
            )}
            {selectedShop.premiumActivatedAt && (
              <div className="detail-row">
                <span>Premium Activated At:</span>
                <strong>{formatFirestoreDate(selectedShop.premiumActivatedAt)}</strong>
              </div>
            )}
            {selectedShop.premiumDeactivatedAt && (
              <div className="detail-row">
                <span>Premium Deactivated At:</span>
                <strong>{formatFirestoreDate(selectedShop.premiumDeactivatedAt)}</strong>
              </div>
            )}
          </div>

          {/* ✅ FCM Token Section */}
          {selectedShop.fcmToken && (
            <div className="detail-section">
              <h5>FCM Token</h5>
              <div className="fcm-token-container">
                <div className="fcm-token-display">
                  <code>{selectedShop.fcmToken}</code>
                </div>
                <button 
                  className="btn-copy-token"
                  onClick={() => copyFCMToken(selectedShop.fcmToken)}
                >
                  {copiedToken === selectedShop.fcmToken ? '✅ Copied!' : '📋 Copy'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Activation History */}
        {selectedShop.activationHistory && selectedShop.activationHistory.length > 0 && (
          <div className="history-section">
            <h5>Activation History ({selectedShop.activationHistory.length})</h5>
            <div className="history-list">
              {selectedShop.activationHistory.slice(-10).reverse().map((entry, idx) => (
                <div key={idx} className="history-item">
                  <div className="history-plan">{entry.planName || entry.action || 'Plan Change'}</div>
                  <div className="history-date">
                    {entry.activatedAt ? formatFirestoreDate(entry.activatedAt) : 
                     entry.deactivatedAt ? formatFirestoreDate(entry.deactivatedAt) : 'N/A'}
                  </div>
                  <div className="history-duration">{entry.duration || 'N/A'}</div>
                  <div className="history-by">{entry.activatedBy || entry.deactivatedBy || 'Admin'}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="modal-footer">
        <button 
          className="btn btn-secondary"
          onClick={() => setShowShopModal(false)}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

      {/* Footer Actions */}
      <div className="footer-actions">
        <button onClick={fetchShops} className="btn-refresh">
          🔄 Refresh Data
        </button>
        <div className="stats-info">
          Showing {filteredShops.length} of {shops.length} shops
        </div>
      </div>
    </div>
  );
};

export default AdminRetailDashboard;